import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLocations } from "@/hooks/useLocations";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import AreaField from "@/components/common/AreaField";
import RichTextEditor from "@/components/common/RichTextEditor";
import MultiImageUploader from "@/components/common/MultiImageUploader";
import CategoryBreadcrumbChip from "@/components/common/CategoryBreadcrumbChip";
import { Loader2, Plus, Trash2, Wand2, Tag, CheckCircle2 } from "lucide-react";

const EMPTY_OPTION = { name: "", mrp: "", price: "" };

/**
 * Escape a plain-text field for safe seeding into a rich-text editor.
 * Preserves paragraph breaks by mapping `\n` → `<br>`. The shared backend
 * will re-sanitize on submit, so this is defensive only.
 */
function textToHtml(text) {
  if (!text) return "";
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
}

const numOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Post Your Deal — legacy-parity submission form (iter-215).
 * Spec: `WEB_MOBILE_SPEC_POST_DEAL.md`.
 *
 * v2 additions on top of iter-214:
 *   • deal_options[] repeater (Name, MRP, Price) — top-level price optional
 *   • website + top-level phone
 *   • 4 rich-text editors: highlights, fine_print (Ad Conditions),
 *     opening_hours, description
 *   • Multi-image (thumb + up to 8 total)
 *   • GrabOne auto-fill box (`POST /classifieds/deals/extract-from-url`,
 *     ~20-60 s) — hydrates every field including options[] and images.
 */
export default function PostDealPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { data: regions = [] } = useLocations();
  const [searchParams] = useSearchParams();

  // Iter-216: chooser forwards category/subcategory ids we echo back on submit.
  const categoryId = searchParams.get("category_id") || null;
  const subcategoryId = searchParams.get("subcategory_id") || null;

  // Iter 754 — `?edit=<dealId>` reuses this form to edit an existing deal.
  // Prefill via GET /deals/{id}/vendor; save via PUT (status untouched —
  // a live deal stays live with the new content).
  const editId = searchParams.get("edit") || null;
  const isEdit = !!editId;

  const [form, setForm] = useState({
    country: "NZ",
    title: "",
    website: "",
    contact_phone: "",
    whatsapp: "",
    contact_email: user?.email || "",
    posted_by: user?.full_name || user?.email || "",
    description: "",
    description_html: "",
    highlights: "",
    highlights_html: "",
    fine_print: "",
    fine_print_html: "",
    opening_hours: "",
    opening_hours_html: "",
    deal_options: [{ ...EMPTY_OPTION }],
    price_label: "",
    images: [],
    region: "",
    city: "",
    area: "",
    pincode: "",
    address: "",
    cta_href: "",
  });
  const [grabUrl, setGrabUrl] = useState("");
  const [submitted, setSubmitted] = useState(null);

  // Iter 754 — hydrate the form from the vendor copy of the deal.
  const editQ = useQuery({
    queryKey: ["deal-vendor", editId],
    enabled: isEdit,
    retry: false,
    queryFn: async () => (await sharedApi.get(`/classifieds/deals/${editId}/vendor`)).data,
  });
  useEffect(() => {
    const d = editQ.data;
    if (!d) return;
    setForm((f) => ({
      ...f,
      title: d.title || "",
      website: d.website || "",
      contact_phone: d.contact_phone || "",
      whatsapp: d.whatsapp || "",
      contact_email: d.contact_email || f.contact_email,
      posted_by: d.posted_by || f.posted_by,
      description: d.description || "",
      description_html: d.description_html || textToHtml(d.description || ""),
      highlights: d.highlights || "",
      highlights_html: d.highlights_html || textToHtml(d.highlights || ""),
      fine_print: d.fine_print || "",
      fine_print_html: d.fine_print_html || textToHtml(d.fine_print || ""),
      opening_hours: d.opening_hours || "",
      opening_hours_html: d.opening_hours_html || textToHtml(d.opening_hours || ""),
      deal_options:
        Array.isArray(d.deal_options) && d.deal_options.length
          ? d.deal_options.map((o) => ({ name: o.name || "", mrp: o.mrp ?? "", price: o.price ?? "" }))
          : [{ ...EMPTY_OPTION }],
      price_label: d.price_label || "",
      images: Array.isArray(d.images) ? d.images : [],
      region: d.region || "",
      city: d.city || "",
      area: d.area || "",
      pincode: d.pincode || "",
      address: d.address || "",
      cta_href: d.cta_href || "",
    }));
  }, [editQ.data]);

  const cities = useMemo(
    () => regions.find((r) => r.region === form.region)?.cities || [],
    [regions, form.region],
  );

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setOpt = (i, patch) =>
    setForm((f) => ({
      ...f,
      deal_options: f.deal_options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)),
    }));
  const addOpt = () =>
    setForm((f) => ({ ...f, deal_options: [...f.deal_options, { ...EMPTY_OPTION }] }));
  const removeOpt = (i) =>
    setForm((f) => ({
      ...f,
      deal_options:
        f.deal_options.length > 1 ? f.deal_options.filter((_, idx) => idx !== i) : f.deal_options,
    }));

  // --- GrabOne auto-fill ---------------------------------------------------
  const extractMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post(
        "/classifieds/deals/extract-from-url",
        { url: grabUrl.trim() },
        { timeout: 90_000 }, // GrabOne scrape can take 20–60 s
      );
      return data;
    },
    onSuccess: (data) => {
      const d = data?.deal || {};
      const nextImages = Array.isArray(data?.images) ? data.images.slice(0, 8) : [];
      // Hydrate ALL known fields; seed rich editors from the plain text so
      // the WYSIWYG shows the content while the sanitizer accepts the HTML.
      setForm((f) => ({
        ...f,
        title: d.title || f.title,
        website: d.website || f.website,
        contact_phone: d.contact_phone || f.contact_phone,
        contact_email: d.contact_email || f.contact_email,
        whatsapp: d.whatsapp || f.whatsapp,
        posted_by: d.posted_by || f.posted_by,
        description: d.description || "",
        description_html: textToHtml(d.description || ""),
        highlights: d.highlights || "",
        highlights_html: textToHtml(d.highlights || ""),
        fine_print: d.fine_print || "",
        fine_print_html: textToHtml(d.fine_print || ""),
        opening_hours: d.opening_hours || "",
        opening_hours_html: textToHtml(d.opening_hours || ""),
        address: d.address || f.address,
        region: d.region || f.region,
        city: d.city || f.city,
        area: d.area || f.area,
        price_label: d.price_label || f.price_label,
        deal_options:
          Array.isArray(d.deal_options) && d.deal_options.length
            ? d.deal_options.map((o) => ({
                name: o.name || "",
                mrp: o.mrp ?? "",
                price: o.price ?? "",
              }))
            : f.deal_options,
        images: nextImages.length ? nextImages : f.images,
      }));
      toast.success("Auto-filled from GrabOne — review and edit before submitting.");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || "Couldn't auto-fill from that link");
    },
  });

  // --- Submit --------------------------------------------------------------
  const submitMut = useMutation({
    mutationFn: async () => {
      // Trim rich HTML shells the editor may leave when empty; the backend
      // auto-derives plain mirrors when omitted.
      const strip = (html) => (html || "").replace(/<[^>]+>/g, "").trim();
      const body = {
        ...form,
        // Numeric coercion — the backend accepts null but rejects "".
        // Only fully-valid rows (name + numeric MRP + numeric price) are
        // included in the submit body; partial rows are silently dropped so
        // the UI enablement rule, HTML5 required rule, and payload filter
        // stay consistent with each other.
        deal_options: form.deal_options
          .map((o) => ({
            name: o.name.trim(),
            mrp: numOrNull(o.mrp),
            price: numOrNull(o.price),
          }))
          .filter((o) => o.name && o.mrp != null && o.price != null),
      };
      // Empty-editor guard for the 4 rich fields.
      ["description_html", "highlights_html", "fine_print_html", "opening_hours_html"].forEach(
        (k) => {
          if (!strip(body[k])) body[k] = undefined;
        },
      );
      // Cull empty top-level scalars so the shared backend keeps its
      // "everything except title is optional" contract clean.
      ["website", "contact_phone", "whatsapp", "contact_email", "posted_by", "price_label", "region", "city", "area", "pincode", "address", "cta_href"].forEach(
        (k) => {
          if (!body[k]) body[k] = undefined;
        },
      );
      // Iter-216: attach chosen category/subcategory ids from the chooser.
      // Backend defaults to the Create Deal root if omitted.
      if (categoryId) body.category_id = categoryId;
      if (subcategoryId) body.subcategory_id = subcategoryId;
      // Iter 754 — editing replaces content only; status stays untouched.
      if (isEdit) {
        const { data } = await sharedApi.put(`/classifieds/deals/${editId}/vendor`, body);
        return data;
      }
      const { data } = await sharedApi.post("/classifieds/deals/submit", body);
      return data;
    },
    onSuccess: (data) => {
      if (isEdit) {
        toast.success("Deal updated");
        nav("/my/deals");
        return;
      }
      setSubmitted({ id: data.id, message: data.message });
      toast.success(data.message || "Thanks! Your deal is under review.");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || (isEdit ? "Couldn't save your changes" : "Couldn't submit deal"));
    },
  });

  // --- Validation ----------------------------------------------------------
  const validOptions = form.deal_options.filter(
    (o) => o.name.trim() && numOrNull(o.mrp) != null && numOrNull(o.price) != null,
  );
  const canSubmit =
    !!form.title.trim() &&
    form.images.length > 0 &&
    validOptions.length > 0 &&
    !submitMut.isPending;

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.images[0]) {
      toast.error("Please upload at least a thumb image.");
      return;
    }
    if (!validOptions.length) {
      toast.error("Please fill in at least one Deal Option row (name + MRP + price).");
      return;
    }
    submitMut.mutate();
  };

  // --- Success screen ------------------------------------------------------
  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-16 text-center" data-testid="post-deal-success">
        <Seo title="Deal submitted" />
        <div className="mx-auto w-16 h-16 rounded-full grid place-items-center bg-emerald-50 text-emerald-600">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-black text-slate-900">
          Thanks — your deal is under review
        </h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          {submitted.message ||
            "We'll notify you as soon as our team reviews your submission (usually within 24 hours)."}
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <Link
            to="/my/deals"
            data-testid="post-deal-success-my-deals"
            className="rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5"
          >
            Go to My deals
          </Link>
          <Link
            to="/deals"
            className="rounded-full border border-soft text-slate-700 hover:border-[color:var(--brand-blue)] font-semibold px-5 py-2.5"
          >
            Back to deals
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="post-deal-page">
      <Seo title={isEdit ? "Edit your deal" : "Post your deal"} description="Post a deal for review — reach thousands of Kiwi buyers." />
      <Breadcrumbs items={[{ label: "Deals", to: "/deals" }, { label: isEdit ? "Edit your deal" : "Post your deal" }]} />

      <h1 className="mt-6 font-display text-3xl md:text-4xl font-black text-slate-900">
        {isEdit ? "Edit your deal" : "Post your deal"}
      </h1>
      {isEdit && editQ.isLoading && (
        <div className="mt-4 inline-flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your deal…
        </div>
      )}
      {isEdit && editQ.isError && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="post-deal-edit-error">
          Couldn&apos;t load this deal for editing — it may not be yours.{" "}
          <Link to="/my/deals" className="font-bold underline">Back to My deals</Link>
        </div>
      )}
      <CategoryBreadcrumbChip
        categoryId={categoryId}
        subcategoryId={subcategoryId}
        fallbackLabel="Create Deal"
        testId="post-deal-change-category"
      />
      <p className="mt-2 text-slate-500 leading-relaxed">
        Approved deals appear on the Deals page. Review usually within 24 hours.
      </p>

      {/* GrabOne auto-fill (new deals only) ------------------------------ */}
      {!isEdit && (
      <div
        className="mt-8 rounded-2xl border border-[color:var(--brand-blue)]/25 bg-[color:var(--brand-blue-wash)]/60 p-4 md:p-5"
        data-testid="post-deal-grabone-card"
      >
        <div className="flex items-center gap-2 font-display font-bold text-slate-900">
          <Wand2 className="w-4 h-4 text-[color:var(--brand-blue)]" />
          Have this deal on GrabOne? Paste the link — Auto-Fill
        </div>
        <p className="mt-1 text-xs text-slate-500">
          We&apos;ll extract title, description, highlights, price options, and images — takes about 20–60 seconds.
        </p>
        <div className="mt-3 flex items-center gap-2 flex-col sm:flex-row">
          <input
            data-testid="post-deal-grabone-url"
            className="input flex-1"
            type="url"
            value={grabUrl}
            onChange={(e) => setGrabUrl(e.target.value)}
            placeholder="https://new.grabone.co.nz/..."
          />
          <button
            type="button"
            data-testid="post-deal-grabone-btn"
            onClick={() => extractMut.mutate()}
            disabled={!grabUrl.trim() || extractMut.isPending}
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] disabled:opacity-40 text-white font-bold px-5 py-2.5 whitespace-nowrap"
          >
            {extractMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {extractMut.isPending ? "Extracting…" : "Auto-fill"}
          </button>
        </div>
      </div>
      )}

      <form onSubmit={onSubmit} className="mt-8 space-y-6">
        {/* Basic details */}
        <div className="grid gap-4">
          <Field label="Ad title" required>
            <input
              data-testid="post-deal-title"
              className="input"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="e.g. Professional Roof Paint Package"
              maxLength={160}
              required
            />
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Website" hint="Merchant's own site (optional)">
              <input
                type="url"
                data-testid="post-deal-website"
                className="input"
                value={form.website}
                onChange={(e) => set({ website: e.target.value })}
                placeholder="https://…"
                maxLength={500}
              />
            </Field>
            <Field label="Phone">
              <input
                data-testid="post-deal-phone"
                className="input"
                value={form.contact_phone}
                onChange={(e) => set({ contact_phone: e.target.value })}
                placeholder="+64 21 …"
              />
            </Field>
          </div>
        </div>

        {/* Rich-text sections */}
        <div className="pt-4 border-t border-subtle">
          <h2 className="font-display text-xl font-bold text-slate-900">Content</h2>
          <p className="mt-1 text-sm text-slate-500">Same editor as Events — bold, italic, underline, strike, colour, highlight, sizes.</p>

          <div className="mt-4 space-y-4">
            <Field label="Ad highlights" hint="What's included · bullet list works well">
              <RichTextEditor
                testId="post-deal-highlights"
                value={form.highlights_html}
                onChange={(html) => set({ highlights_html: html })}
                onPlainChange={(text) => set({ highlights: text })}
                placeholder="• 60-min service · • Certified staff · • Free re-do…"
                maxLength={20000}
              />
            </Field>
            <Field label="Ad conditions" hint="T&Cs shown as fine-print on the deal detail page">
              <RichTextEditor
                testId="post-deal-fineprint"
                value={form.fine_print_html}
                onChange={(html) => set({ fine_print_html: html })}
                onPlainChange={(text) => set({ fine_print: text })}
                placeholder="Redemption window, exclusions, one-per-customer rules…"
                maxLength={20000}
              />
            </Field>
            <Field label="Opening hours" hint="Tue–Sat 9am–6pm">
              <RichTextEditor
                testId="post-deal-hours"
                value={form.opening_hours_html}
                onChange={(html) => set({ opening_hours_html: html })}
                onPlainChange={(text) => set({ opening_hours: text })}
                placeholder="Mon–Fri 9am–6pm · Sat 10am–4pm · closed Sun"
                maxLength={20000}
              />
            </Field>
            <Field label="Ad description">
              <RichTextEditor
                testId="post-deal-description"
                value={form.description_html}
                onChange={(html) => set({ description_html: html })}
                onPlainChange={(text) => set({ description: text })}
                placeholder="Tell buyers what makes this deal great"
                maxLength={60000}
              />
            </Field>
          </div>
        </div>

        {/* Deal options repeater */}
        <div className="pt-4 border-t border-subtle">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-slate-900 inline-flex items-center gap-2">
              <Tag className="w-5 h-5 text-[color:var(--brand-orange)]" /> Deal options
            </h2>
            <span className="text-xs text-slate-500">Min 1 required</span>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Buyers see these as a purchase-options table. Backend derives the headline price from the cheapest row.
          </p>

          <div className="mt-4 space-y-3">
            {form.deal_options.map((o, i) => (
              <div
                key={i}
                data-testid={`post-deal-option-${i}`}
                className="grid grid-cols-12 gap-2 items-start rounded-xl border border-soft bg-white p-3"
              >
                <div className="col-span-12 md:col-span-6">
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">Option name *</label>
                  <input
                    className="input"
                    value={o.name}
                    onChange={(e) => setOpt(i, { name: e.target.value })}
                    placeholder="e.g. Half head of foils"
                  />
                </div>
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">MRP *</label>
                  <input
                    className="input"
                    type="number"
                    inputMode="decimal"
                    value={o.mrp}
                    onChange={(e) => setOpt(i, { mrp: e.target.value })}
                    placeholder="135"
                    min="0"
                  />
                </div>
                <div className="col-span-6 md:col-span-3">
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">Price *</label>
                  <input
                    className="input"
                    type="number"
                    inputMode="decimal"
                    value={o.price}
                    onChange={(e) => setOpt(i, { price: e.target.value })}
                    placeholder="69"
                    min="0"
                  />
                </div>
                <div className="col-span-12 md:col-span-1 flex md:justify-end">
                  <button
                    type="button"
                    onClick={() => removeOpt(i)}
                    disabled={form.deal_options.length <= 1}
                    data-testid={`post-deal-option-remove-${i}`}
                    aria-label={`Remove option ${i + 1}`}
                    className="w-9 h-9 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:pointer-events-none grid place-items-center md:mt-6"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addOpt}
            data-testid="post-deal-option-add"
            className="mt-3 inline-flex items-center gap-2 rounded-full border border-dashed border-[color:var(--brand-blue)] text-[color:var(--brand-blue)] font-semibold px-4 py-2 hover:bg-[color:var(--brand-blue-wash)]"
          >
            <Plus className="w-4 h-4" /> Add option
          </button>
        </div>

        {/* Images */}
        <div className="pt-4 border-t border-subtle">
          <h2 className="font-display text-xl font-bold text-slate-900">Photos</h2>
          <p className="mt-1 text-sm text-slate-500">
            First image is the thumb (required). Up to 8 total.
          </p>
          <div className="mt-4">
            <MultiImageUploader
              images={form.images}
              onChange={(imgs) => set({ images: imgs })}
              testId="post-deal-image"
              max={8}
            />
          </div>
        </div>

        {/* Location */}
        <div className="pt-4 border-t border-subtle">
          <h2 className="font-display text-xl font-bold text-slate-900">Where to redeem</h2>
          <div className="mt-3 grid md:grid-cols-2 gap-3">
            <Field label="Region" compact>
              <select
                className="input"
                data-testid="post-deal-region"
                value={form.region}
                onChange={(e) => set({ region: e.target.value, city: "", area: "" })}
              >
                <option value="">Select region</option>
                {regions.map((r) => (
                  <option key={r.region} value={r.region}>{r.region}</option>
                ))}
              </select>
            </Field>
            <Field label="City" compact>
              <select
                className="input"
                data-testid="post-deal-city"
                value={form.city}
                onChange={(e) => set({ city: e.target.value, area: "" })}
                disabled={!cities.length}
              >
                <option value="">Select city</option>
                {cities.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </Field>
            <Field label="Suburb / area" compact hint="Auto-suggests known suburbs">
              <AreaField
                testId="post-deal-area"
                region={form.region}
                city={form.city}
                country={form.country}
                value={form.area}
                onChange={(v) => set({ area: v })}
              />
            </Field>
            <Field label="Pincode" compact>
              <input
                data-testid="post-deal-pincode"
                className="input"
                value={form.pincode}
                onChange={(e) => set({ pincode: e.target.value })}
                maxLength={16}
              />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Merchant address" compact>
              <input
                data-testid="post-deal-address"
                className="input"
                value={form.address}
                onChange={(e) => set({ address: e.target.value })}
              />
            </Field>
          </div>
        </div>

        {/* Submit */}
        <div className="pt-4 border-t border-subtle flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            {isEdit
              ? "Your changes replace the deal's content — its live/review status stays as-is."
              : "Your deal will be hidden until an admin approves it. We'll notify you either way."}
          </p>
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="post-deal-submit"
            className="inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:pointer-events-none text-white font-bold px-6 py-3"
          >
            {submitMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? "Save changes" : "Post now"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, hint, compact, children }) {
  return (
    <label className="block">
      <span className={`block font-semibold text-slate-700 ${compact ? "text-sm mb-1" : "text-sm mb-1.5"}`}>
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}
